import z from "zod";
import { TestEventSchema } from "../TestEventSchema";
import {
  SegmentResults,
  TemplateResults,
  TestEventResults,
  TestResult,
  TestResults,
  WelderResults,
} from "@/api/williams/testEvents";

type Path = (string | number)[];

const TestResultsSchema = z
  .object({
    testResultId: z.string(),
    testId: z.string(),
    testType: z.string(),
    acceptanceCriteria: z.string().nullish(),
    result: z.nativeEnum(TestResult).nullish(),
  })
  .transform((data) => data as TestResults);

const SegmentResultsSchema = z
  .object({
    segmentId: z.string().nullish(),
    segmentNumber: z.number().nullish(),
    testDate: z.string().nullish(),
    comment: z.string().nullish(),
    tests: z.array(TestResultsSchema),
    result: z.nativeEnum(TestResult).nullish(),
  })
  .transform((data) => data as SegmentResults);

const TemplateResultsSchema = z
  .object({
    recordId: z.string(),
    templateNumber: z.string(),
    standard: z.number(),
    segments: z.array(SegmentResultsSchema),
    submitted: z.boolean(),
    result: z.nativeEnum(TestResult).nullish(),
  })
  .transform((data) => data as TemplateResults);

const WelderResultsSchema = z
  .object({
    welderId: z.string(),
    welderName: z.string(),
    templates: z.array(TemplateResultsSchema),
  })
  .transform((data) => data as WelderResults);

export const TestEventResultsSchema = z
  .object({
    testEvent: TestEventSchema,
    welders: z.array(WelderResultsSchema),
  })
  .transform((data) => data as TestEventResults)
  .superRefine(validateForm);

function validateForm(results: TestEventResults, context: z.RefinementCtx) {
  results.welders.forEach((welder, index) => {
    validateWelder(welder, context, ["welders", index]);
  });
}

function validateWelder(welder: WelderResults, context: z.RefinementCtx, path: Path) {
  welder.templates.forEach((template, index) => {
    if (!template.submitted) {
      validateTemplate(template, context, [...path, "templates", index]);
    }
  });
}

function validateTemplate(template: TemplateResults, context: z.RefinementCtx, path: Path) {
  if (template.result === TestResult.None) {
    raiseError(context, path, "result");
  } else if (template.segments.some((segment) => !checkSegmentResult(segment))) {
    raiseError(context, path, "result");
  } else if (template.segments.every((segment) => segment.result === TestResult.NotConducted)) {
    raiseError(context, path, "result");
  }

  if (template.segments.some((segment) => segment.result !== TestResult.NotConducted && !segment.testDate)) {
    raiseError(context, path, "testDate");
  }

  template.segments.forEach((segment, index) => {
    validateSegment(segment, context, [...path, "segments", index]);
  });
}

function validateSegment(segment: SegmentResults, context: z.RefinementCtx, path: Path) {
  if (!checkSegmentResult(segment)) {
    raiseError(context, path, "result");
  }
  if (!segment.comment && segment.result === TestResult.Fail) {
    raiseError(context, path, "comment");
  }
  if (!segment.testDate && segment.result !== TestResult.NotConducted) {
    raiseError(context, path, "testDate");
  }

  segment.tests.forEach((test, index) => {
    validateTest(test, context, [...path, "tests", index]);
  });
}

function validateTest(test: TestResults, context: z.RefinementCtx, path: Path) {
  if (test.result === TestResult.None) {
    raiseError(context, path, "result");
  }
}

function checkSegmentResult(segment: SegmentResults): boolean {
  if (segment.result === TestResult.None) {
    return false;
  } else if (segment.tests.some((test) => test.result === TestResult.None)) {
    return false;
  } else if (segment.result === TestResult.Fail && segment.tests.every((test) => test.result !== TestResult.Fail)) {
    return false;
  } else if (segment.result === TestResult.Fail && !segment.comment) {
    return false;
  } else {
    return true;
  }
}

function raiseError(context: z.RefinementCtx, path: Path, type: "result" | "testDate" | "comment") {
  context.addIssue({
    path: [...path, type],
    code: z.ZodIssueCode.custom,
  });
}
